import { useEffect, useId, useState } from "react";
import { useField, useFormikContext } from "formik";
import { FiAlertCircle, FiChevronDown, FiEye, FiEyeOff } from "react-icons/fi";

/** Move focus to the first invalid control so the member sees what to fix. */
export function focusFirstInvalid() {
  setTimeout(() => {
    const el = document.querySelector('.ax [aria-invalid="true"]');
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    }
  }, 30);
}

/** Drop inside a <Formik> form: after a failed submit, focus the first invalid control. */
export function FocusFirstError() {
  const { isSubmitting, submitCount, errors } = useFormikContext();
  useEffect(() => {
    if (submitCount > 0 && !isSubmitting && Object.keys(errors).length > 0) {
      focusFirstInvalid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, submitCount]);
  return null;
}

export function ErrorText({ id, children }) {
  if (!children) return null;
  return (
    <p className="ax-error" id={id} role="alert">
      <FiAlertCircle aria-hidden="true" size={16} />
      <span>{children}</span>
    </p>
  );
}

/** Label, control, hint and error wired together with the right ids. */
function Frame({ id, label, labelAside, optional, hint, error, children }) {
  return (
    <div className="ax-field">
      {label ? (
        <div className="ax-label">
          <label htmlFor={id}>
            {label}
            {optional ? <span className="ax-optional"> (optional)</span> : null}
          </label>
          {labelAside}
        </div>
      ) : null}
      {children}
      {hint && !error ? (
        <p className="ax-hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      <ErrorText id={`${id}-error`}>{error}</ErrorText>
    </div>
  );
}

function useBound(name, id) {
  const [field, meta] = useField(name);
  const error = meta.touched && meta.error ? meta.error : "";
  return {
    field,
    error,
    aria: {
      id,
      "aria-invalid": error ? "true" : undefined,
      "aria-describedby": error ? `${id}-error` : `${id}-hint`,
    },
  };
}

export function TextField({
  name,
  label,
  labelAside,
  optional,
  hint,
  icon: Icon,
  end,
  type = "text",
  className = "",
  ...inputProps
}) {
  const id = useId();
  const { field, error, aria } = useBound(name, id);
  return (
    <Frame id={id} label={label} labelAside={labelAside} optional={optional} hint={hint} error={error}>
      <div className={`ax-control${Icon ? " ax-control--icon" : ""}${end ? " ax-control--end" : ""}`}>
        {Icon ? (
          <span className="ax-icon" aria-hidden="true">
            <Icon size={18} />
          </span>
        ) : null}
        <input
          className={`ax-input ${className}`.trim()}
          type={type}
          {...field}
          {...aria}
          {...inputProps}
        />
        {end ? <span className="ax-end">{end}</span> : null}
      </div>
    </Frame>
  );
}

export function PasswordField({ name, label = "Password", icon, hint, labelAside, ...rest }) {
  const [shown, setShown] = useState(false);
  return (
    <TextField
      name={name}
      label={label}
      labelAside={labelAside}
      hint={hint}
      icon={icon}
      type={shown ? "text" : "password"}
      autoCapitalize="none"
      spellCheck={false}
      end={
        <button
          type="button"
          className="ax-iconbtn"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
        >
          {shown ? <FiEyeOff size={18} aria-hidden="true" /> : <FiEye size={18} aria-hidden="true" />}
        </button>
      }
      {...rest}
    />
  );
}

export function SelectField({
  name,
  label,
  options = [],
  placeholder = "Select",
  hint,
  optional,
  disabled,
  onValueChange,
}) {
  const id = useId();
  const { field, error, aria } = useBound(name, id);
  return (
    <Frame id={id} label={label} optional={optional} hint={hint} error={error}>
      <div className="ax-control ax-control--select">
        <select
          className="ax-input"
          {...field}
          {...aria}
          disabled={disabled}
          onChange={(e) => {
            field.onChange(e);
            if (onValueChange) onValueChange(e.target.value);
          }}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="ax-select-chev" aria-hidden="true">
          <FiChevronDown size={18} />
        </span>
      </div>
    </Frame>
  );
}

export function PhoneField({ name, label, hint, icon, optional }) {
  return (
    <TextField
      name={name}
      label={label}
      hint={hint}
      icon={icon}
      optional={optional}
      type="tel"
      inputMode="tel"
      autoComplete={name === "phone" ? "tel" : "off"}
      placeholder="0801 234 5678"
    />
  );
}

export function DateField({ name, label, hint, max }) {
  return (
    <TextField
      name={name}
      label={label}
      hint={hint}
      type="date"
      min="1900-01-01"
      max={max}
      autoComplete="bday"
    />
  );
}
